import Fuse from 'fuse.js'

import type { ESignSpecialisation } from '@/modules/appointment/types/e-sign.types'

export type RankedResult = {
  /**
   * The specialisation object where the match was found
   */
  specialisation: ESignSpecialisation
  /**
   * The type of match that was found
   */
  matchType:
    | 'title-exact'
    | 'title-partial'
    | 'keyword-partial'
    | 'doctor-partial'
    | 'title-fuzzy'
    | 'keyword-fuzzy'
    | 'doctor-fuzzy'
    | 'unknown'
  /**
   * The term that was matched
   */
  matchedTerm?: string

  /**
   * Related items to the match (e.g. keywords, doctors)
   */
  descriptionItems: string[]

  /**
   * The score of the match
   */
  matchScore?: number
}

/**
 * Flattens specialisations for search, including keywords and doctor names.
 * - Adds the specialisation title (nl), all keywords (nl), and all doctor names (with 'Dr.' prefix removed) as search terms.
 * @param specialisations - Array of specialisation objects
 * @returns Array of objects with { term, specialisation, type }
 */
export const flattenSpecialisationData = (specialisations: ESignSpecialisation[]) => {
  return specialisations.map((specialisation) => {
    // New field for full normalized doctor names, suitable for tokenized fuzzy search
    const searchableDoctorFullNames = specialisation.doctors.map((doctor) => {
      const name = doctor.name ?? ''
      const nameWithoutPrefix = name.replace(/^dr\.?\s+/i, '')
      return normalizeText(nameWithoutPrefix) // e.g., "germonpre eric"
    })

    return {
      term: normalizeText(specialisation.title.nl),
      keywords: specialisation.searchKeywords?.nl.map((k) => normalizeText(k)) ?? [],
      doctors: specialisation.doctors.map((doctor) => normalizeText(doctor.name ?? '')) ?? [],
      searchableDoctorFullNames, // Full names for fuzzy search, e.g., ['germonpre eric']
      specialisation,
    }
  })
}

/**
 * Normalizes text by removing diacritics and converting to lowercase
 */
export const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036F]/g, '') // Remove diacritics
    .replaceAll(/[^\d\sa-z]/g, '') // Remove special characters
    .replaceAll(/^dr\.?\s+/g, '') // Remove 'dr.' prefix
    .replaceAll(/^prof\.?\s+/g, '') // Remove 'prof.' prefix
    .trim()
}

export const searchSpecialisations = (query: string, specialisations: ESignSpecialisation[]): RankedResult[] => {
  // If the query is empty return an empty array
  if (!query.trim()) return []

  const normalizedQuery = normalizeText(query)
  const flattened = flattenSpecialisationData(specialisations)
  const seen = new Set<number>()
  const resultMap = new Map<number, RankedResult>()

  const hasConsecutiveMatch = (text: string, query: string): boolean => {
    if (query.length < 3) return false
    return text.includes(query)
  }

  // STEP 1 — Exact match on title = immediate return
  for (const { term, specialisation } of flattened) {
    if (term === normalizedQuery && !resultMap.has(specialisation.id)) {
      resultMap.set(specialisation.id, {
        specialisation,
        matchType: 'title-exact',
        matchedTerm: specialisation.title.nl,
        matchScore: 1,
        descriptionItems: specialisation.description?.nl ? [specialisation.description.nl] : [],
      })
    }
  }

  // STEP 2 — Exact or partial matches (non-fuzzy)
  for (const { term, keywords, specialisation } of flattened) {
    if (seen.has(specialisation.id)) continue

    // Title partial match
    if (hasConsecutiveMatch(term, normalizedQuery)) {
      const descriptionItems = getRelatedKeywordsOrDoctors(normalizedQuery, specialisation)

      if (!resultMap.has(specialisation.id)) {
        resultMap.set(specialisation.id, {
          specialisation,
          matchType: 'title-partial',
          matchedTerm: specialisation.title.nl,
          matchScore: 0.9,
          descriptionItems,
        })
      }
      seen.add(specialisation.id)
    }

    // Doctor partial match
    const doctorMatch = specialisation.doctors.find((d) => {
      const searchableName = normalizeText(d.name ?? '')
      return hasConsecutiveMatch(searchableName, normalizedQuery)
    })
    if (doctorMatch) {
      const descriptionItems = getRelatedKeywordsOrDoctors(normalizedQuery, specialisation)

      if (!resultMap.has(specialisation.id)) {
        resultMap.set(specialisation.id, {
          specialisation,
          matchType: 'doctor-partial',
          matchedTerm: doctorMatch.name,
          matchScore: 0.8,
          descriptionItems,
        })
      }
      seen.add(specialisation.id)
    }

    // Keyword partial match
    const keywordMatch = keywords.find((k) => hasConsecutiveMatch(k, normalizedQuery))
    if (keywordMatch) {
      const descriptionItems = getRelatedKeywordsOrDoctors(normalizedQuery, specialisation)

      if (!resultMap.has(specialisation.id)) {
        resultMap.set(specialisation.id, {
          specialisation,
          matchType: 'keyword-partial',
          matchedTerm: keywordMatch,
          matchScore: 0.8,
          descriptionItems,
        })
      }
      seen.add(specialisation.id)
    }
  }

  console.log('resultMap', resultMap)
  // STEP 3 — If anything matched, return now
  if (resultMap.size > 0) {
    const typePriority = (type: string): number => {
      return (
        {
          'title-partial': 1,
          'keyword-partial': 2,
          'doctor-partial': 3,
        }[type] ?? 99
      )
    }

    const results = [...resultMap.values()]
    results.sort((a, b) => {
      const priA = typePriority(a.matchType)
      const priB = typePriority(b.matchType)
      if (priA !== priB) return priA - priB
      return (b.matchScore ?? 0) - (a.matchScore ?? 0)
    })
    return results
  }

  // STEP 4 — Fuzzy match as fallback
  if (normalizedQuery.length >= 3) {
    const fuse = new Fuse(flattened, {
      keys: [
        { name: 'term', weight: 2 },
        { name: 'keywords', weight: 0.4 },
        { name: 'doctors', weight: 0.4 },
        { name: 'searchableDoctorFullNames', weight: 0.4 },
      ],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      shouldSort: true,
      minMatchCharLength: 3,
      isCaseSensitive: false,
      useExtendedSearch: true,
    })

    const fuseResults = fuse.search(normalizedQuery)

    for (const { item, score, matches } of fuseResults) {
      if (seen.has(item.specialisation.id)) continue

      const matchField = matches?.[0]?.key ?? 'unknown'
      let matchType: RankedResult['matchType'] = 'unknown'

      switch (matchField) {
        case 'term': {
          matchType = 'title-fuzzy'
          break
        }
        case 'keywords': {
          matchType = 'keyword-fuzzy'
          break
        }
        case 'searchableDoctorFullNames':
        case 'doctors': {
          matchType = 'doctor-fuzzy'

          break
        }
      }

      if (matchType === 'unknown') {
        continue
      }

      const descriptionItems = getRelatedKeywordsOrDoctors(normalizedQuery, item.specialisation, fuseResults)

      if (!resultMap.has(item.specialisation.id)) {
        resultMap.set(item.specialisation.id, {
          specialisation: item.specialisation,
          matchType,
          matchedTerm: matches?.[0]?.value,
          matchScore: score ?? undefined,
          descriptionItems,
        })
      }

      seen.add(item.specialisation.id)
    }
  }

  return [...resultMap.values()]
}

/**
 * Returns the 3 most relevant keywords or doctors for a given query.
 * @param specialisation
 * @param query
 * @returns
 */
const getRelatedKeywordsOrDoctors = (
  query: string,
  specialisation: ESignSpecialisation,
  results?: Fuse.FuseResult<{
    term: string
    keywords: string[]
    doctors: string[]
    searchableDoctorFullNames: string[]
    specialisation: ESignSpecialisation
  }>[],
): string[] => {
  // If we have results, use the best matching keywords/doctors from there
  if (results?.length) {
    const bestMatch = results[0]
    const matches = bestMatch.matches ?? []
    const matchedItems = matches
      .map((match) => {
        const refIndex = match.refIndex
        if (refIndex === undefined) return null

        if (match.key === 'keywords') return bestMatch.item.keywords[refIndex]
        if (match.key === 'doctors' || match.key === 'searchableDoctorFullNames') {
          const doctor = specialisation.doctors[refIndex]
          return doctor?.name ?? null
        }
        return null
      })
      .filter((item): item is string => item !== null)
      .slice(0, 3)

    if (matchedItems.length > 0) return matchedItems
  }

  // Fallback to return partial or exact match
  const keywords = specialisation.searchKeywords?.nl ?? []
  const doctors = specialisation.doctors.map((doctor) => doctor.name).filter(Boolean) ?? []

  const allItems = [...keywords, ...doctors]
  const normalizedQuery = normalizeText(query)

  const matches = allItems.filter((item) => normalizeText(item).includes(normalizedQuery)).slice(0, 3)

  if (matches.length > 0) return matches

  return specialisation.description?.nl ? [specialisation.description.nl] : []
}

/**
 * Returns highlighting info for a string given a query.
 * - If the query matches the text (exact, partial, or fuzzy), returns segment info for highlighting.
 * - Used for rendering highlighted search results.
 * @param text - The text to highlight
 * @param query - The search query
 * @returns Object with shouldHighlight and segments for rendering
 */
export const getHighlightInfo = (
  text: string,
  query: string,
): {
  text: string
  shouldHighlight: boolean
  segments?: Array<{ text: string; highlight: boolean }>
} => {
  if (!query.trim()) return { text, shouldHighlight: false }

  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()

  const index = lowerText.indexOf(lowerQuery)
  if (index >= 0) {
    const before = text.slice(0, index)
    const match = text.slice(index, index + query.length)
    const after = text.slice(index + query.length)
    return {
      text,
      shouldHighlight: true,
      segments: [
        { text: before, highlight: false },
        { text: match, highlight: true },
        { text: after, highlight: false },
      ],
    }
  }

  return { text, shouldHighlight: false }
}
